import { Link } from 'react-router-dom';
import { EnrollmentWithDetails, getProgramTypeIcon, selectedDaysOf } from '../types/program';
import { getBillingTypeDisplay, getProgramTypeColors, formatPaymentMethod, isLineItemProrated } from '../utils/billingDisplay';
import { formatDateDisplay } from '../utils/date';
import { useStatusLabel, useProgramTypeName, useLabels } from '../config/useLabels';
import { BrandBadge } from './BrandBadge';
import { useCartHeldInvoiceIds } from '../hooks/useCartHeldInvoiceIds';
import { isPaymentInFlight } from '../utils/cart';

export interface Payment {
  id: number | string;
  amount: number;
  payment_method: string;
  payment_date: string;
  status: string;
  bank_account_setup_intent_id?: string;
  bank_account_setup_intents?: {
    id: string;
    bank_name?: string;
    account_type?: string;
    last4?: string;
  };
}

export interface Invoice {
  id: string;
  enrollment_id: string;
  total_amount: number;
  amount_paid: number;
  status: 'pending' | 'partial' | 'paid' | 'overdue' | 'voided';
  due_date: string;
  period_start: string;
  period_end: string;
  is_prorated: boolean;
  created_at: string;
  updated_at: string;
  payments: Payment[];
}

export interface InvoiceWithDetails extends Invoice {
  enrollments: EnrollmentWithDetails;
}

export interface LineItem {
  id: string;
  description?: string;
  amount: number;
  line_item_config_id?: string;
  line_item_configs?: {
    name?: string;
    type?: string;
    subtype?: string;
  };
}

function getStatusColor(status: string) {
  switch (status.toLowerCase()) {
    case 'paid': return 'bg-green-100 text-green-800';
    case 'partial': return 'bg-blue-100 text-blue-800';
    case 'pending': return 'bg-yellow-100 text-yellow-800';
    case 'overdue': return 'bg-red-100 text-red-800';
    default: return 'bg-gray-100 text-gray-800';
  }
}

function getLineItemTypeColor(type?: string) {
  if (!type) return 'bg-gray-100 text-gray-800';
  switch (type.toLowerCase()) {
    case 'tuition': return 'bg-blue-100 text-blue-800';
    case 'fee': return 'bg-purple-100 text-purple-800';
    case 'discount': return 'bg-yellow-100 text-yellow-800';
    case 'refund': return 'bg-green-100 text-green-800';
    default: return 'bg-gray-100 text-gray-800';
  }
}

interface InvoiceDetailsBodyProps {
  invoice: InvoiceWithDetails;
  lineItems?: LineItem[];
  isLineItemsLoading?: boolean;
  /** When provided, renders the "Pay Now" button (parent portal only). */
  onPayNow?: () => void;
  /** When provided, renders the "View Enrollment" button. */
  onViewEnrollment?: () => void;
  /** When true, renders "View wallet" links for BHCS Cash / wallet payments (parent portal only). */
  showWalletLink?: boolean;
}

/**
 * Presentational invoice detail card shared by the parent Invoice Details page
 * and the admin Invoice Details modal, so both surfaces show identical info.
 * Action buttons (Pay Now, View Enrollment, wallet links) are opt-in via props.
 */
export function InvoiceDetailsBody({
  invoice,
  lineItems,
  isLineItemsLoading,
  onPayNow,
  onViewEnrollment,
  showWalletLink,
}: InvoiceDetailsBodyProps) {
  const statusLabel = useStatusLabel();
  const programTypeName = useProgramTypeName();
  const t = useLabels();
  // In-flight: any payment that has been submitted but not yet settled or failed.
  // This covers both pure-ACH (invoice stays 'pending') and ACH+BHCS Cash split
  // (invoice moves to 'partial' once the wallet portion is applied), and a bank bundle
  // payment still clearing, which has no payments row until the money lands.
  const cartHeldInvoiceIds = useCartHeldInvoiceIds({ invoiceId: invoice.id });
  const isPaymentProcessing = isPaymentInFlight(invoice, cartHeldInvoiceIds);

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <div className="p-4 sm:p-6 border-b">
        {/* Status at top, with the brand that sold the class. The badge hides itself in the
            admin portal and whenever the parent portal is single-brand. */}
        <div className="mb-3">
          <div className="flex flex-wrap items-center gap-2">
            <div className={`inline-block px-3 py-1.5 rounded-full text-sm font-semibold ${isPaymentProcessing ? 'bg-blue-100 text-blue-800' : getStatusColor(invoice.status)}`}>
              {statusLabel(isPaymentProcessing ? 'payment_processing' : invoice.status)}
            </div>
            <BrandBadge brand={invoice.enrollments.programs.brand_key} />
          </div>
          {isPaymentProcessing && (
            <p className="text-xs text-blue-600 mt-2">Your payment has been submitted and is being processed. This usually takes 3–5 business days for bank transfers.</p>
          )}
        </div>
        <div className="flex items-start justify-between gap-4">
          {/* Left: Who and what */}
          <div className="min-w-0 flex-1">
            <div className="text-xs text-gray-500 mb-1 break-words">Invoice #{invoice.id}</div>
            <h1 className="text-lg sm:text-xl md:text-2xl font-semibold text-gray-900">
              {invoice.enrollments.students.first_name} {invoice.enrollments.students.last_name}
            </h1>
            <p className="text-xs sm:text-sm text-gray-700 mt-1">
              {(() => {
                const md = (invoice.enrollments as unknown as { metadata?: Record<string, unknown> }).metadata || {};
                const lvlUnknown = (md as Record<string, unknown>).level;
                const lvl = typeof lvlUnknown === 'string' && lvlUnknown.trim() ? lvlUnknown : '';
                return lvl ? `${invoice.enrollments.programs.name} (${lvl})` : invoice.enrollments.programs.name;
              })()}
            </p>
            <div className="flex flex-wrap gap-1.5 mt-2">
              <span className="inline-flex items-center text-xs bg-gray-100 text-gray-600 rounded-full px-2 py-0.5">
                {formatDateDisplay(invoice.period_start)} – {formatDateDisplay(invoice.period_end)}
              </span>
              <span className="inline-flex items-center text-xs bg-gray-100 text-gray-600 rounded-full px-2 py-0.5">
                Due {formatDateDisplay(invoice.due_date)}
              </span>
            </div>
          </div>
          {/* Right: Status and balance */}
          <div className="flex items-start md:items-end md:text-right gap-4 shrink-0 md:justify-end">
            <div className="md:order-1">
              <div className="text-xs text-gray-600">{t('label.balanceDue')}</div>
              <div className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900">
                ${(invoice.total_amount - invoice.amount_paid).toFixed(2)}
              </div>
              {typeof invoice.total_amount === 'number' && (
                <div className="text-xs text-gray-600 mt-1">Total ${invoice.total_amount.toFixed(2)}</div>
              )}
              {onPayNow && invoice.status !== 'paid' && invoice.status.toLowerCase() !== 'voided' && !isPaymentProcessing && (
                <button
                  className="mt-2 w-full md:w-auto bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                  onClick={onPayNow}
                >
                  Pay Now
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Program Details */}
      <div className="p-4 sm:p-6 border-b">
        <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-4">{t('header.programDetails')}</h3>
        <div className="flex items-start space-x-4">
          {getProgramTypeIcon(invoice.enrollments.programs.type)}
          <div>
            <h4 className="text-base sm:text-lg font-medium text-gray-900">
              {(() => {
                const md = (invoice.enrollments as unknown as { metadata?: Record<string, unknown> }).metadata || {};
                const lvlUnknown = (md as Record<string, unknown>).level;
                const lvl = typeof lvlUnknown === 'string' && lvlUnknown.trim() ? lvlUnknown : '';
                return lvl ? `${invoice.enrollments.programs.name} (${lvl})` : invoice.enrollments.programs.name;
              })()}
            </h4>
            <div className="flex flex-wrap gap-1.5 mt-1">
              <span className={`inline-flex items-center text-xs rounded-full px-2 py-0.5 ${getProgramTypeColors(invoice.enrollments.programs.type)}`}>
                {programTypeName(invoice.enrollments.programs.type)}
              </span>
              <span className="inline-flex items-center gap-1 text-xs bg-gray-100 text-gray-600 rounded-full px-2 py-0.5">
                {(() => { const { label, Icon } = getBillingTypeDisplay(invoice.enrollments.programs.billing_type); return <><Icon className="w-3 h-3" />{label}</>; })()}
              </span>
              <span className="inline-flex items-center text-xs bg-gray-100 text-gray-600 rounded-full px-2 py-0.5">
                {invoice.enrollments.programs.start_date ? formatDateDisplay(invoice.enrollments.programs.start_date) : 'N/A'} – {invoice.enrollments.programs.end_date ? formatDateDisplay(invoice.enrollments.programs.end_date) : 'N/A'}
              </span>
            </div>
            {invoice.enrollments.programs.billing_type === 'per_day' && selectedDaysOf(invoice.enrollments).length > 0 && (
              <div className="mt-3">
                <span className="text-xs sm:text-sm text-gray-500">Selected Dates ({selectedDaysOf(invoice.enrollments).length} days):</span>
                <div className="mt-2 flex flex-wrap gap-2">
                  {selectedDaysOf(invoice.enrollments).map((date: string, index: number) => (
                    <span key={index} className="inline-block bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs sm:text-sm font-medium">
                      {formatDateDisplay(date)}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {onViewEnrollment && (
              <div className="mt-3">
                <button
                  onClick={onViewEnrollment}
                  className="bg-gray-500 text-white px-2 py-1 rounded text-xs hover:bg-gray-600 flex items-center space-x-1"
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  <span>View Enrollment</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Invoice Line Items */}
      {isLineItemsLoading ? (
        <div className="p-6">Loading line items...</div>
      ) : lineItems && lineItems.length > 0 ? (
        <div className="p-4 sm:p-6 border-b">
          <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-3">{t('header.lineItems')}</h3>
          <div className="divide-y divide-gray-100">
            {lineItems.map((item: LineItem) => {
              const isItemProrated = isLineItemProrated(
                Boolean(invoice.is_prorated),
                item.line_item_configs,
              );

              return (
                <div key={item.id} className="flex items-start justify-between py-2.5 gap-3">
                  <div className="flex flex-col gap-1 min-w-0 flex-1">
                    <span className="text-sm text-gray-800">{item.description}</span>
                    <div className="flex flex-wrap gap-1">
                      {item.line_item_configs?.type && (
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${getLineItemTypeColor(item.line_item_configs.type)}`}>
                          {item.line_item_configs.type}
                        </span>
                      )}
                      {item.line_item_configs?.subtype && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500">
                          {item.line_item_configs.subtype.replace(/_/g, ' ')}
                        </span>
                      )}
                      {isItemProrated && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-orange-100 text-orange-700">
                          prorated
                        </span>
                      )}
                    </div>
                  </div>
                  <span className={`text-sm font-medium shrink-0 ${item.amount < 0 ? 'text-green-600' : 'text-gray-900'}`}>
                    {item.amount < 0 ? '-' : ''}${Math.abs(item.amount).toFixed(2)}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      ) : null}

      {/* Payment History */}
      <div className="p-4 sm:p-6">
        <div className="pt-6 mt-2">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base sm:text-lg font-medium text-gray-900">{t('header.paymentHistory')}</h3>
          </div>
          {invoice.payments && invoice.payments.length > 0 ? (
            <div className="divide-y divide-gray-200">
              {invoice.payments.map((payment) => (
                <div key={payment.id} className="py-4 flex items-start justify-between">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 text-sm flex-wrap">
                      <span className="font-medium text-gray-800">
                        {formatPaymentMethod(payment.payment_method, payment.bank_account_setup_intents)}
                      </span>
                      {showWalletLink && (payment.payment_method === 'bhcs_cash' || payment.payment_method === 'wallet') && (
                        <Link to="/wallet" className="text-blue-600 hover:underline text-xs">View wallet ↗</Link>
                      )}
                    </div>
                    <div className="text-xs text-gray-500">{formatDateDisplay(payment.payment_date)}</div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-sm font-medium text-gray-900">${payment.amount.toFixed(2)}</div>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(payment.status)}`}>
                      {payment.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs sm:text-sm text-gray-500 text-center py-4">No payment history available</p>
          )}
        </div>
      </div>
    </div>
  );
}
